import { useMutation, useQueryClient } from '@tanstack/react-query';

import { notificationQueries, readNotification } from '../api/notification';
import useNotificationApiInstance from './useNotificationApiInstance';

import type { AxiosError } from 'axios';
import type { InfiniteData, UseMutationOptions } from '@tanstack/react-query';
import type { GetNotificationFeedResponse } from '../constants';

type NotificationFeedCache = InfiniteData<GetNotificationFeedResponse, string | undefined>;

type UseReadNotificationOptions = UseMutationOptions<void, AxiosError, string>;

/**
 * 단일 Notification 읽음 처리 (알림 카드 클릭).
 * @description
 * - 성공하면 `notificationQueries.all()` 을 invalidate 해서 feed/badge 를 최신화.
 */
const useReadNotification = (options?: UseReadNotificationOptions) => {
  const queryClient = useQueryClient();
  const { instance } = useNotificationApiInstance();

  return useMutation({
    mutationFn: (id: string) => readNotification(instance, id),
    ...options,
    onMutate: async id => {
      const queryKey = [...notificationQueries.all(), 'feed'] as const;

      await queryClient.cancelQueries({ queryKey });

      queryClient.setQueriesData<NotificationFeedCache>({ queryKey }, old => {
        if (!old) return old;

        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            data: page.data.map(notification =>
              notification.id === id ? { ...notification, read: true } : notification,
            ),
          })),
        };
      });

      await options?.onMutate?.(id);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: notificationQueries.all() });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export default useReadNotification;
